if (!customElements.get('expected-delivery')) {
  class ExpectedDelivery extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.init();
      } else {
        window.initLazyScript(this, this.init.bind(this));
      }
    }

    init() {
      this.notice = this.querySelector('.js-expected-delivery-notice');
      this.variantData = this.getVariantData();
      this.smLocation = this.dataset.smLocation;
      this.textTemplate = this.dataset.text;
      this.offsetDays = parseInt(this.dataset.offsetDays, 10) || 0;
      this.defaultText = this.parentElement && this.parentElement.querySelector('.js-inventory-status-text');

      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      this.update(parseInt(this.dataset.currentVariantId, 10));
    }

    toggleDefaultText(hide) {
      if (this.defaultText) this.defaultText.hidden = hide;
    }

    // Parses the per-variant JSON payload once
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantData || JSON.parse(dataEl.textContent);
    }

    handleVariantChange(evt) {
      if (evt.detail.variant) {
        this.update(evt.detail.variant.id);
      } else {
        this.hidden = true;
        this.toggleDefaultText(false);
      }
    }

    // Finds the SM available stock for a variant, null if not found
    getSmStock(entry) {
      const locations = entry.stock && entry.stock.locations;
      if (!Array.isArray(locations)) return null;
      const loc = locations.find((item) => item.location_name === this.smLocation);
      return loc ? loc.available : null;
    }

    // Returns the dispatch date (base + 1 calendar day) or null when not applicable
    resolveDate(entry) {
      // Skip dropshipping / local-stock items; only fjernlager should announce a date
      if (!entry.isFjernlager) return null;

      const sm = this.getSmStock(entry);
      // Only relevant when SM has no available stock
      if (sm === null || sm > 0) return null;

      const deliveries = Array.isArray(entry.deliveries) ? entry.deliveries : null;
      if (!deliveries || deliveries.length === 0) return null;

      // Peak may stop syncing after PO close, leaving stale past dates; null dates
      // carry no announceable arrival. Only future-dated incoming counts.
      const now = new Date();
      const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const future = deliveries.filter(
        (item) => item.expectedDeliveryDate && item.expectedDeliveryDate >= todayIso
      );
      if (future.length === 0) return null;

      // Oldest first
      const sorted = future.sort(
        (a, b) => a.expectedDeliveryDate.localeCompare(b.expectedDeliveryDate)
      );

      // Accumulate incoming on top of the negative stock; pick the date that turns it positive
      let cumulative = sm;
      let baseDate = null;
      for (const item of sorted) {
        cumulative += item.quantity;
        if (cumulative > 0) {
          baseDate = item.expectedDeliveryDate;
          break;
        }
      }
      // Incoming never covers the deficit -> still announce the latest known restock date
      if (!baseDate) baseDate = sorted[sorted.length - 1].expectedDeliveryDate;

      return this.resolveDispatchDate(baseDate);
    }

    // "2026-05-19" -> "20/5" (base + offsetDays, then pushed to Monday if it
    // lands on Fri/Sat/Sun; no leading zero, no year)
    resolveDispatchDate(isoDate) {
      const [y, m, d] = isoDate.split('-').map(Number);
      const date = new Date(Date.UTC(y, m - 1, d));
      date.setUTCDate(date.getUTCDate() + this.offsetDays);

      const day = date.getUTCDay(); // 0=Sun, 1=Mon ... 6=Sat
      if (day === 5) date.setUTCDate(date.getUTCDate() + 3); // Fri -> Mon
      else if (day === 6) date.setUTCDate(date.getUTCDate() + 2); // Sat -> Mon
      else if (day === 0) date.setUTCDate(date.getUTCDate() + 1); // Sun -> Mon

      return `${date.getUTCDate()}/${date.getUTCMonth() + 1}`;
    }

    update(variantId) {
      const entry = this.variantData.find((v) => v.id === variantId);
      const date = entry ? this.resolveDate(entry) : null;

      if (!date) {
        this.hidden = true;
        this.toggleDefaultText(false);
        return;
      }

      this.notice.textContent = this.textTemplate.replace('[date]', date);
      this.hidden = false;
      this.toggleDefaultText(true);
    }
  }

  customElements.define('expected-delivery', ExpectedDelivery);
}
